import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { OperationHistoryData } from '@models/medical-history.model';
import { AlertService } from '@services/alert.service';
import { DisplayService } from '@services/display.service';
import { MedicalHistoryService } from '@services/medical-history.service';
import { BehaviorSubject, take } from 'rxjs';

@Component({
  selector: 'gc-operation-history',
  templateUrl: './operation-history.component.html',
  styleUrls: ['./operation-history.component.scss'],
  standalone: false,
})
export class OperationHistoryComponent implements OnInit, OnChanges {
  @Input() userId: number;
  @Input() operationHistory: OperationHistoryData[] = [];
  @Input() requestUuid: string | null = null;
  @Output() submittedStep = new EventEmitter<{ step: string; values: any }>();

  operationHistoryForm: FormGroup;
  historyListForm: FormGroup;
  panelOpenState = false;
  rowIndex: number;
  isMobile$: BehaviorSubject<boolean>;

  constructor(
    private readonly fb: FormBuilder,
    private readonly medicalHistoryService: MedicalHistoryService,
    private readonly alertService: AlertService,
    private readonly displayService: DisplayService,
  ) {}

  ngOnInit(): void {
    this.buildForms();
    this.isMobile$ = this.displayService.isMobile$;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['operationHistory'] && !changes['operationHistory'].firstChange) {
      this.buildForms();
    }
  }

  buildForms(): void {
    this.operationHistoryForm = this.initRow({
      operation: '',
      date: null,
      doctor: '',
      hospital: '',
      note: '',
    });

    this.operationHistory = this.operationHistory ? this.operationHistory : [];
    this.historyListForm = this.fb.group({
      rowList: this.fb.array(this.operationHistory.map((operation) => this.initRow(operation))),
    });
  }

  initRow(operation: OperationHistoryData): FormGroup {
    return this.fb.group({
      operation: [operation.operation || '', Validators.required],
      date: [operation.date || '', Validators.required],
      doctor: [operation.doctor || ''],
      hospital: [operation.hospital || ''],
      note: [operation.note || ''],
    });
  }

  get rowListControl(): FormArray {
    return this.historyListForm.get('rowList') as FormArray;
  }

  checkDateValid(control: AbstractControl): boolean {
    if (control.touched && control.value === '') {
      return true;
    }
    return false;
  }

  addToList() {
    if (this.operationHistoryForm.valid) {
      this.rowListControl.push(this.initRow(this.operationHistoryForm.value));
      this.operationHistoryForm.reset();
    }
  }

  deleteRow(index: number) {
    this.rowListControl.removeAt(index);
  }

  saveToRequest() {
    this.medicalHistoryService
      .saveRequestMedicalHistory$(this.requestUuid, 'operation_history', this.rowListControl.value)
      .pipe(take(1))
      .subscribe((request) => {
        if (request) {
          this.submittedStep.emit({
            step: 'operationHistory',
            values: this.rowListControl.value,
          });
          this.alertService.snackbar('operation_history_saved');
        }
      });
  }

  saveToProfile(): void {
    this.medicalHistoryService
      .saveMedicalHistory$(this.userId, 'operation_history', { user_id: this.userId, operation_history: this.rowListControl.value })
      .subscribe(() => this.alertService.snackbar('operation_history_saved'));
  }

  submit() {
    if (this.requestUuid) {
      this.saveToRequest();
    } else {
      this.saveToProfile();
    }
  }
}
